import { useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { AnimatePresence, m } from "framer-motion";
import { Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { COUNTRY_PHONES, findCountryPhone, PhoneInput } from "@/components/ui/phone-input";
import { ResponsiveSheet } from "@/components/ui/responsive-sheet";

export interface EmergencyContact {
  name: string;
  phone: string;
  relationship: string;
}

interface EmergencyContactModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentContacts: EmergencyContact[];
  onSave: (contacts: EmergencyContact[]) => Promise<void>;
}

const EMPTY_CONTACT: EmergencyContact = { name: "", phone: "", relationship: "" };

/**
 * Split an existing E164 phone (e.g. "+40712345678") into a country code +
 * local digits via longest-prefix match. If no prefix matches (legacy local
 * format), default to "ro" and treat the whole value as digits.
 */
function splitE164(phone: string): { country: string; digits: string } {
  const trimmed = phone.trim();
  if (!trimmed.startsWith("+")) {
    return { country: "ro", digits: trimmed.replace(/\D/g, "") };
  }
  const sorted = [...COUNTRY_PHONES].sort((a, b) => b.prefix.length - a.prefix.length);
  for (const c of sorted) {
    if (trimmed.startsWith(c.prefix)) {
      return { country: c.code, digits: trimmed.slice(c.prefix.length).replace(/\D/g, "") };
    }
  }
  return { country: "ro", digits: trimmed.replace(/\D/g, "") };
}

export function EmergencyContactModal({
  open,
  onOpenChange,
  currentContacts,
  onSave,
}: EmergencyContactModalProps) {
  const { t } = useLingui();
  const seed: EmergencyContact[] =
    currentContacts.length > 0 ? currentContacts : [{ ...EMPTY_CONTACT }];
  const [contacts, setContacts] = useState<EmergencyContact[]>(() =>
    seed.map((c) => {
      // Normalize stored phone to local digits for the editor; we re-compose
      // E164 on save using the per-row country code.
      const { digits } = splitE164(c.phone || "");
      return { ...c, phone: digits };
    }),
  );
  const [phoneCountries, setPhoneCountries] = useState<string[]>(() =>
    seed.map((c) => splitE164(c.phone || "").country),
  );
  const [saving, setSaving] = useState(false);

  const updateContact = (index: number, field: keyof EmergencyContact, value: string) => {
    setContacts((prev) => prev.map((c, i) => (i === index ? { ...c, [field]: value } : c)));
  };

  const setCountry = (index: number, code: string) => {
    setPhoneCountries((prev) => prev.map((c, i) => (i === index ? code : c)));
  };

  const addContact = () => {
    setContacts((prev) => [...prev, { ...EMPTY_CONTACT }]);
    setPhoneCountries((prev) => [...prev, "ro"]);
  };

  const removeContact = (index: number) => {
    setContacts((prev) => prev.filter((_, i) => i !== index));
    setPhoneCountries((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    // Compose E164 (prefix + digits) per row, then filter out empty contacts.
    const composed = contacts.map((c, i) => {
      const digits = (c.phone || "").replace(/\D/g, "");
      const phone = digits ? `${findCountryPhone(phoneCountries[i]).prefix}${digits}` : "";
      return { ...c, phone };
    });
    const valid = composed.filter((c) => c.name.trim() && c.phone.trim());
    setSaving(true);
    try {
      await onSave(valid);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  const canSave =
    contacts.some((c) => c.name.trim() && (c.phone || "").replace(/\D/g, "").length > 0) ||
    contacts.length === 0;

  return (
    <ResponsiveSheet
      open={open}
      onClose={() => onOpenChange(false)}
      title={<Trans>Emergency contacts</Trans>}
      description={<Trans>People to contact in case of an emergency.</Trans>}
      width="md"
      ariaLabel={t`Emergency contacts`}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="min-h-[44px]">
            <Trans>Cancel</Trans>
          </Button>
          <Button onClick={handleSave} disabled={saving || !canSave} className="min-h-[44px]">
            {saving ? <Trans>Saving…</Trans> : <Trans>Save</Trans>}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <AnimatePresence mode="popLayout">
          {contacts.map((contact, i) => (
            <m.div
              key={i}
              layout
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="border-border relative space-y-3 rounded-xl border p-3"
            >
              {contacts.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeContact(i)}
                  aria-label={t`Remove contact`}
                  className="text-muted-foreground hover:text-destructive absolute top-2 right-2 flex size-7 items-center justify-center rounded-md transition-colors"
                >
                  <Trash2 className="size-3.5" />
                </button>
              )}
              <div>
                <label className="text-muted-foreground mb-1 block text-xs font-medium">
                  <Trans>Name</Trans>
                </label>
                <Input
                  value={contact.name}
                  onChange={(e) => updateContact(i, "name", e.target.value)}
                  placeholder={t`Full name`}
                  className="h-10"
                />
              </div>
              <div>
                <label className="text-muted-foreground mb-1 block text-xs font-medium">
                  <Trans>Phone</Trans>
                </label>
                <PhoneInput
                  value={contact.phone}
                  onChange={(v) => updateContact(i, "phone", v)}
                  countryCode={phoneCountries[i]}
                  onCountryChange={(code) => setCountry(i, code)}
                  placeholder={t`712 345 678`}
                />
              </div>
              <div>
                <label className="text-muted-foreground mb-1 block text-xs font-medium">
                  <Trans>Relationship</Trans>
                </label>
                <Input
                  value={contact.relationship}
                  onChange={(e) => updateContact(i, "relationship", e.target.value)}
                  placeholder={t`Spouse, parent, friend…`}
                  className="h-10"
                />
              </div>
            </m.div>
          ))}
        </AnimatePresence>

        <Button variant="outline" size="sm" onClick={addContact} className="w-full gap-1.5">
          <Plus className="size-4" />
          <Trans>Add contact</Trans>
        </Button>
      </div>
    </ResponsiveSheet>
  );
}
